import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { CATEGORY_META, PHOTO_SUBTYPE_LABEL, humanSize } from "../lib/files";
import { Loader2, Download, X, FileText } from "lucide-react";

// Subtype display order (mirrors backend SUBTYPE_PRIORITY in patients.py)
const SUBTYPE_ORDER = [
  "full_face",
  "profile_left",
  "profile_right",
  "smile_full",
  "smile_relaxed",
  "retracted_anterior",
  "retracted_posterior",
  "occlusal_upper",
  "occlusal_lower",
  "study_model_mounted",
  "wax_up",
  "other_clinical",
];

// Group label → header copy shown above the thumbnails
const GROUP_LABELS = {
  facial: "Facial & Smile Photographs",
  intraoral: "Intraoral & Occlusal Photographs",
  study_model: "Mounted Study Models & Wax-ups",
  radiograph: "Radiographs",
  realguide: "Real Guide Planning Exports",
  other: "Other Files",
};

function groupOf(f) {
  if ((f.category || "").startsWith("realguide_")) return "realguide";
  if (f.category === "study_model") return "study_model";
  if (f.category === "clinical_photo") {
    const s = f.photo_subtype || "";
    if (["full_face", "profile_left", "profile_right", "smile_relaxed", "smile_full"].includes(s)) {
      return "facial";
    }
    return "intraoral";
  }
  if (f.category === "radiograph_2d") return "radiograph";
  return "other";
}

const isImageFile = (f) =>
  ["radiograph_2d", "clinical_photo", "study_model"].includes(f.category);

export default function CaseImageGallery({ analysisId }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blobs, setBlobs] = useState({}); // file_id -> object URL
  const [lightbox, setLightbox] = useState(null); // file object or null

  useEffect(() => {
    let cancelled = false;
    const created = [];
    (async () => {
      try {
        const r = await api.get(`/case-analysis/${analysisId}/files`);
        if (cancelled) return;
        setFiles(r.data);
        // Fetch image bytes for each image file (so the <img src> can be a blob URL
        // — keeps every read on the audit-logged backend route).
        const urls = {};
        for (const f of r.data) {
          if (!isImageFile(f)) continue;
          try {
            const res = await api.get(`/files/${f.id}/download`, {
              responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            urls[f.id] = url;
            created.push(url);
            if (cancelled) {
              created.forEach((u) => URL.revokeObjectURL(u));
              return;
            }
            setBlobs((prev) => ({ ...prev, [f.id]: url }));
          } catch (_) {
            /* ignore single-file failures */
          }
        }
      } catch (_) {
        /* gallery is non-critical; silently skip */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [analysisId]);

  const grouped = useMemo(() => {
    const g = { facial: [], intraoral: [], study_model: [], radiograph: [], realguide: [], other: [] };
    for (const f of files) g[groupOf(f)].push(f);
    // Sort each group by subtype priority then filename
    for (const k of Object.keys(g)) {
      g[k].sort((a, b) => {
        const ai = SUBTYPE_ORDER.indexOf(a.photo_subtype || "");
        const bi = SUBTYPE_ORDER.indexOf(b.photo_subtype || "");
        if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return a.original_filename.localeCompare(b.original_filename);
      });
    }
    return g;
  }, [files]);

  if (loading) {
    return (
      <div
        className="mb-10 p-6 bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg flex items-center gap-2 text-xs text-[var(--pa-text-muted)]"
        data-testid="cca-gallery-loading"
      >
        <Loader2 size={14} className="animate-spin text-[var(--pa-gold)]" />
        Loading imaging gallery…
      </div>
    );
  }
  if (files.length === 0) return null;

  const renderGroup = (key) => {
    const items = grouped[key];
    if (!items.length) return null;
    return (
      <div
        key={key}
        className="mt-5"
        data-testid={`cca-gallery-group-${key}`}
      >
        <div className="kicker text-[var(--pa-text-muted)] mb-2">{GROUP_LABELS[key]}</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((f) => {
            const meta = CATEGORY_META[f.category] || CATEGORY_META.other;
            const Icon = meta.Icon;
            const url = blobs[f.id];
            const isImg = isImageFile(f);
            const subLabel = PHOTO_SUBTYPE_LABEL[f.photo_subtype || ""] || meta.label;
            return (
              <button
                type="button"
                key={f.id}
                data-testid={`cca-gallery-thumb-${f.id}`}
                onClick={() => isImg && url && setLightbox({ ...f, url })}
                className={`group relative rounded-md border border-[var(--pa-border)] bg-[var(--pa-bg-2)] overflow-hidden text-left transition hover:border-[var(--pa-gold)]/60 ${
                  isImg && url ? "cursor-zoom-in" : "cursor-default"
                }`}
                title={f.original_filename}
              >
                <div className="aspect-square w-full bg-[var(--pa-bg)] flex items-center justify-center">
                  {isImg && url ? (
                    <img
                      src={url}
                      alt={subLabel}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-3 text-center">
                      <Icon size={28} style={{ color: meta.color }} />
                      <span className="text-[10px] text-[var(--pa-text-muted)] uppercase tracking-wider">
                        {meta.label}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-2 text-[10px] leading-tight">
                  <div className="text-[var(--pa-text)] truncate">{subLabel}</div>
                  <div className="text-[var(--pa-text-dim)] truncate" title={f.original_filename}>
                    {f.original_filename} · {humanSize(f.size)}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <section className="mb-10" data-testid="cca-gallery">
      <div className="flex items-baseline gap-3">
        <div className="kicker">Imaging the AI reviewed</div>
        <span className="text-[10px] text-[var(--pa-text-dim)]">
          {files.length} file{files.length === 1 ? "" : "s"} · click any thumbnail to zoom
        </span>
      </div>
      {["facial", "intraoral", "study_model", "radiograph", "realguide", "other"].map(renderGroup)}

      {lightbox && (
        <div
          data-testid="cca-gallery-lightbox"
          role="dialog"
          aria-modal="true"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            data-testid="cca-gallery-lightbox-close"
            className="absolute top-4 right-4 p-2 rounded-full bg-[var(--pa-surface)] border border-[var(--pa-border)] text-[var(--pa-text)] hover:bg-[var(--pa-bg-2)] transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <div
            className="max-w-[92vw] max-h-[92vh] flex flex-col items-center gap-3"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightbox.url}
              alt={lightbox.original_filename}
              className="max-w-full max-h-[82vh] object-contain rounded-md border border-[var(--pa-gold)]/40 shadow-2xl"
            />
            <div className="text-xs text-[var(--pa-text)] text-center max-w-xl">
              <div className="font-semibold">
                {PHOTO_SUBTYPE_LABEL[lightbox.photo_subtype || ""] ||
                  (CATEGORY_META[lightbox.category] || CATEGORY_META.other).label}
              </div>
              <div className="text-[var(--pa-text-dim)] mt-0.5">
                {lightbox.original_filename} · {humanSize(lightbox.size)}
              </div>
              <a
                href={lightbox.url}
                download={lightbox.original_filename}
                className="inline-flex items-center gap-1.5 text-[var(--pa-gold)] hover:underline text-[11px] mt-2"
                data-testid="cca-gallery-lightbox-download"
                onClick={(e) => e.stopPropagation()}
              >
                <Download size={11} /> Download original
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
